import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import { isValidToken } from './util.js'
import config from './config.js'
import redux from './redux/index.js'
import socket from './redux/socket-io.js'
import WorkSpace from './WorkSpace.js'
import PdsSpinner from './components/PdsSpinner.js'
import NoAccount from './components/NoAccount.js'
import Feedback from './components/Feedback.js'
import { isOutlook } from './lib/office.js'

/**
 * App is always called with a valid idToken.
 * If we aren't logged in, it means the api token or
 * the access token are invalid.
 */
const App = ({
  error,
  strings,
  idToken,
  token,
  accessToken,
  graphAccessToken,
  sharePointSaveEnabled,
  sharePointTemplatesEnabled,
  showNoAccountPage,
  connectWebsockets,
  onResetError,
  doStartHeartbeat,
  doLogin
}) => {
  let isLoggedIn = isValidToken(idToken) && isValidToken(token)
  if (isLoggedIn) console.info('App: id token and api tokens are valid')

  if (sharePointSaveEnabled || sharePointTemplatesEnabled) {
    isLoggedIn = isLoggedIn && isValidToken(accessToken)
    if (isLoggedIn) console.info('App: access token is valid')
  }

  if (sharePointSaveEnabled && isOutlook()) {
    isLoggedIn = isLoggedIn && isValidToken(graphAccessToken)
    if (isLoggedIn) console.info('App: graph token is valid')
  }

  if (isLoggedIn) console.info('App: all tokens valid')

  // Start heartbeat on initial render.
  useEffect(doStartHeartbeat, [])

  const renderError = () => {
    const errorMessage = () => {
      if (error) {
        console.error(error)
        const { errorType, errorMessage } = error
        const translated = strings[errorType] || 'N/A'
        if (errorMessage) {
          return `${translated} (${errorMessage})`
        } else {
          return translated
        }
      } else {
        return ''
      }
    }
    return (
      <Feedback
        open={!!error}
        message={errorMessage()}
        variant='error'
        closeTimeout={config.errorTimeout}
        onAutoClose={onResetError}
      />
    )
  }

  // (Re-)Connect to websockets if token changed.
  useEffect(() => {
    if (isLoggedIn) {
      connectWebsockets(token)
    }
  }, [token])

  // Auto login if not logged in.
  useEffect(() => {
    if (!isLoggedIn) {
      setTimeout(() => doLogin(idToken), config.autoLoginTimeout)
    }
  }, [isLoggedIn])

  if (showNoAccountPage) {
    return <NoAccount />
  } else {
    return (
      <>
        {renderError()}
        {isLoggedIn ? <WorkSpace /> : <Spinner />}
      </>
    )
  }
}

const Spinner = () => {
  return (
    <div style={alignSpinnerStyle}>
      <PdsSpinner />
    </div>
  )
}

const alignSpinnerStyle = {
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
}

const mapStateToProps = (state) => {
  const {
    token,
    accessToken,
    graphAccessToken,
    sharePointSaveEnabled,
    sharePointTemplatesEnabled
  } = state.login.userData
  return {
    error: state.error,
    strings: state.locale.strings,
    showNoAccountPage: state.login.showNoAccountPage,
    token,
    accessToken,
    graphAccessToken,
    sharePointSaveEnabled,
    sharePointTemplatesEnabled
  }
}

const mapDispatchToProps = (dispatch) => {
  const { error, login } = redux.actions
  return {
    doLogin: (idToken) => {
      dispatch(login.doLogin(idToken))
    },
    doStartHeartbeat: () => {
      dispatch(redux.actions.login.doStartHeartbeat())
    },
    connectWebsockets: (token) => {
      socket.connect(token, dispatch)
    },
    onResetError: () => {
      dispatch(error.resetError())
    }
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(App)
